import { cn, Select } from '../../ui/index.js';

/**
 * Configuration's navigation: pages grouped by what they are about, down the
 * side. A single row of tabs ran to seventeen and wrapped its own labels onto
 * two lines; grouped, a person finds "where do I change the email wording"
 * by its section rather than by scanning a row.
 *
 * On a narrow screen it is one dropdown — the same list, the same groups.
 */

export interface ConfigNavItem {
  value: string;
  label: string;
  count?: number;
  /** False hides it: a page for a feature this group has not got, or a platform page. */
  show?: boolean;
}
export interface ConfigNavSection {
  title: string | null;
  items: ConfigNavItem[];
}

export function ConfigNav({
  sections,
  value,
  onChange,
}: {
  sections: ConfigNavSection[];
  value: string;
  onChange: (v: string) => void;
}) {
  const visible = sections
    .map((s) => ({ ...s, items: s.items.filter((i) => i.show !== false) }))
    .filter((s) => s.items.length > 0);

  return (
    <>
      <div className="lg:hidden">
        <Select
          value={value}
          onValueChange={onChange}
          options={visible.flatMap((s) =>
            s.items.map((i) => ({ value: i.value, label: s.title ? `${s.title} · ${i.label}` : i.label })),
          )}
          aria-label="Configuration page"
        />
      </div>

      <nav aria-label="Configuration" className="hidden lg:block">
        <div className="sticky top-4 space-y-4">
          {visible.map((s, n) => (
            <div key={s.title ?? `section-${n}`}>
              {s.title && (
                <div className="mb-1 px-2 text-3xs font-semibold uppercase tracking-wider text-ink-tertiary">
                  {s.title}
                </div>
              )}
              <ul className="space-y-px">
                {s.items.map((i) => {
                  const active = i.value === value;
                  return (
                    <li key={i.value}>
                      <button
                        type="button"
                        onClick={() => onChange(i.value)}
                        aria-current={active ? 'page' : undefined}
                        className={cn(
                          'flex w-full items-center justify-between gap-2 whitespace-nowrap rounded-md px-2 py-1.5 text-left text-sm',
                          'transition-colors duration-fast focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand',
                          active
                            ? 'bg-brand-tint font-medium text-brand'
                            : 'text-ink-secondary hover:bg-surface-sunken hover:text-ink',
                        )}
                      >
                        <span>{i.label}</span>
                        {i.count !== undefined && (
                          <span
                            className={cn(
                              'nums rounded-xs px-1 text-2xs',
                              active ? 'bg-surface text-brand' : 'bg-surface-sunken text-ink-tertiary',
                            )}
                          >
                            {i.count}
                          </span>
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      </nav>
    </>
  );
}
